import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
  useQuery,
} from "react-query";
import LastCoordsAdapter from "../../../adapters/LastCoordsAdapter";
import { ManualOccurrenceType } from "components/Modals/OccurrenceManagementModal/data";
import { IOccurrence } from "interfaces/Occurrence";
import {
  getManualOccurrence,
  IGetOccurrenceResponse,
} from "services/OccurrenceService";
import { IFormError } from "interfaces/ExpressError";
import { IReport } from "interfaces/Report";

type OccurrenceRefetch = (
  options?:
    | (RefetchOptions & RefetchQueryFilters<IGetOccurrenceResponse>)
    | undefined
) => Promise<QueryObserverResult<IGetOccurrenceResponse | undefined, unknown>>;

type ContextProps = {
  occurrenceType: ManualOccurrenceType;
  occurrenceToEdit: IOccurrence;
  lastSelectedTabLabel: string;
  isFetching: boolean;
  isLoading: boolean;
  setOccurrenceToEditId: Dispatch<SetStateAction<string>>;
  setLastSelectedTabLabel: Dispatch<SetStateAction<string>>;
  refetch: OccurrenceRefetch;
  addError: (key: string, message: string) => void;
  removeError: (field: string) => void;
  changeSelectedReport: (report: IReport) => void
  selectedReport: IReport | null,
};

export const ManualOccurrenceContext = createContext({} as ContextProps);

type ProviderProps = {
  children: ReactNode;
};

function ManualOccurrenceProvider({ children }: ProviderProps) {
  const [lastSelectedTabLabel, setLastSelectedTabLabel] = useState<string>("");
  const [occurrenceType, setOccurrenceType] =
    useState<ManualOccurrenceType>("patrimonial");
  const [errors, setErrors] = useState<IFormError>({});
  const [occurrenceToEditId, setOccurrenceToEditId] = useState<string>("");
  const [occurrenceToEdit, setOccurrenceToEdit] = useState<IOccurrence>(
    {} as IOccurrence
  );
  const [selectedReport, setSelectedReport] = useState<IReport | null>(null)

  const addError = useCallback((key: string, message: string) => {
    setErrors((prev) => ({
      ...prev,
      [key]: message
    }))
  }, [])

  const changeSelectedReport = useCallback((report: IReport) => {
    setSelectedReport(report)
  }, [])

  const removeError = useCallback((field: string) => {
    const _errors = { ...errors };
    delete _errors[field];

    setErrors(() => {
      return _errors;
    });
  }, [errors])

  const { isFetching, refetch, isLoading } = useQuery(
    ["getOccurrence", occurrenceToEditId],
    () => getManualOccurrence(occurrenceToEditId),
    {
      onSuccess: (data) => {
        if (!data) return;
        setOccurrenceToEdit(data.item);
        setOccurrenceType(
          data.item.patrimonialType === 1 ? "patrimonial" : "vehicular"
        );
        setSelectedReport(null);
      },
      refetchOnWindowFocus: false,
    }
  );

  const contextValue = useMemo(
    () => ({
      occurrenceType,
      isFetching,
      isLoading,
      occurrenceToEdit: LastCoordsAdapter.adapt(occurrenceToEdit),
      lastSelectedTabLabel,
      selectedReport,
      setOccurrenceToEditId,
      refetch,
      setLastSelectedTabLabel,
      addError,
      changeSelectedReport,
      removeError
    }),
    [
      occurrenceType,
      occurrenceToEdit,
      isFetching,
      lastSelectedTabLabel,
      isLoading,
      selectedReport,
      setOccurrenceToEditId,
      setLastSelectedTabLabel,
      refetch,
      changeSelectedReport,
      addError,
      removeError
    ]
  );

  return (
    <ManualOccurrenceContext.Provider value={contextValue}>
      {children}
    </ManualOccurrenceContext.Provider>
  );
}

const useManualOccurrence = () => useContext(ManualOccurrenceContext);

export { ManualOccurrenceProvider, useManualOccurrence };
